import { AppMode, JaneDM } from '@iheartjane/dm-sdk';
import { getFlagContext, useFlagClient } from '@iheartjane/feature-flags';
import { statePostalCode, useContextEnsuringProvider } from '@iheartjane/utils';
// eslint-disable-next-line no-restricted-imports
import type { LDClient } from 'launchdarkly-react-client-sdk';
import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { v4 as uuid } from 'uuid';

import { SDKProvider } from '@jane/dm/internal';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import { useKioskUser } from '@jane/kiosk/data-access';
import { useTypedRouteLoaderData } from '@jane/kiosk/hooks';
import { register } from '@jane/kiosk/util';

interface SessionContext {
  flagClient: LDClient | undefined;
  jdid: string;
  resetSession: () => void;
  sessionId: string;
}

const SessionContext = createContext<SessionContext | null>(null);
SessionContext.displayName = 'Session';

export const SessionProvider = ({ children }: PropsWithChildren) => {
  const { store } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: kioskUser } = useKioskUser();

  const flagClient = useFlagClient();
  const [queryParams] = useSearchParams();
  const [sessionId, setSessionId] = useState<string>(
    queryParams.get('sessionId') || uuid()
  );
  const [jdid, setJdid] = useState<string>(
    queryParams.get('jdid') || JaneDM.generateJaneDeviceId()
  );

  useEffect(() => {
    register({ 'Session ID': sessionId });
  }, [sessionId]);

  useEffect(() => {
    async function identifyContext() {
      await flagClient?.identify(
        getFlagContext({
          extraAttributes: {
            appMode: AppMode.Kiosk,
            state: statePostalCode(store?.state),
            storeId: store?.id,
            organizationId: store?.organization_id ?? undefined,
          },
          janeDeviceId: jdid,
          user: { ...kioskUser },
        })
      );
    }
    identifyContext();
  }, [flagClient, store, jdid, kioskUser]);

  const resetSession = useCallback(() => {
    setSessionId(uuid());
    setJdid(JaneDM.generateJaneDeviceId());
  }, []);

  const contextValue = useMemo(
    () => ({ flagClient, resetSession, sessionId, jdid }),
    [flagClient, jdid, resetSession, sessionId]
  );

  return (
    <SessionContext.Provider value={contextValue}>
      <SDKProvider kioskUser={kioskUser} jdid={jdid}>
        {children}
      </SDKProvider>
    </SessionContext.Provider>
  );
};

export const useSessionContext = () =>
  useContextEnsuringProvider(SessionContext);
